import { useState } from "react";

import {
  useGetDepositsHistoryQuery,
  useGetWithdrawalsHistoryQuery,
} from "entities/user";
import { Spiner } from "features/chart";

import type { DateRange, FinanceHistoryTab } from "../model";
import { getHistoryErrorMessage } from "../lib";

import { FinanceHistoryTabs } from "./finance-history-tabs";
import { DateRangePicker } from "./date-range-picker";
import { DepositsTable } from "./deposits-table";
import { WithdrawalsTable } from "./withdrawals-table";
import { HistoryError } from "./history-error";
import styles from "./FinanceHistory.module.css";

export const FinanceHistory = () => {
  const [activeTab, setActiveTab] = useState<FinanceHistoryTab>("deposits");
  const [dateRange, setDateRange] = useState<DateRange | null>(null);

  const queryParams = dateRange
    ? { date_from: dateRange.dateFrom, date_to: dateRange.dateTo }
    : undefined;

  const {
    data: depositsHistory,
    isLoading: isDepositsLoading,
    error: depositsError,
  } = useGetDepositsHistoryQuery(queryParams);
  const {
    data: withdrawalsHistory,
    isLoading: isWithdrawalsLoading,
    error: withdrawalsError,
  } = useGetWithdrawalsHistoryQuery(queryParams);

  const depositsErrorMessage = getHistoryErrorMessage(depositsError);
  const withdrawalsErrorMessage = getHistoryErrorMessage(withdrawalsError);

  return (
    <div className={styles.history}>
      <div className={styles.header}>
        <FinanceHistoryTabs active={activeTab} onChange={setActiveTab} />
        <DateRangePicker
          value={dateRange}
          onApply={setDateRange}
          onReset={() => setDateRange(null)}
        />
      </div>

      <div className={styles.content}>
        {activeTab === "deposits" &&
          (isDepositsLoading ? (
            <Spiner />
          ) : depositsErrorMessage ? (
            <HistoryError message={depositsErrorMessage} />
          ) : (
            <DepositsTable deposits={depositsHistory?.deposits ?? []} />
          ))}

        {activeTab === "withdrawals" &&
          (isWithdrawalsLoading ? (
            <Spiner />
          ) : withdrawalsErrorMessage ? (
            <HistoryError message={withdrawalsErrorMessage} />
          ) : (
            <WithdrawalsTable
              withdrawals={withdrawalsHistory?.withdrawals ?? []}
            />
          ))}
      </div>
    </div>
  );
};
